import { create } from 'zustand';

export interface AuthUser {
  id: string;
  nickname: string;
  avatarUrl: string | null;
  phone: string | null;
  planTier: string;
  creatorStatus?: string | null;
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  setToken: (token: string) => void;
  setAuth: (token: string, user: AuthUser) => void;
  setUser: (user: AuthUser | null) => void;
  logout: () => void;
}

export const AUTH_STORAGE_KEY = 'lumeseek_web_auth_v1';

interface PersistedAuth {
  token: string | null;
  user: AuthUser | null;
}

function parsePersisted(raw: string | null): PersistedAuth {
  if (!raw) return { token: null, user: null };
  try {
    const parsed = JSON.parse(raw);
    return {
      token: typeof parsed.token === 'string' ? parsed.token : null,
      user: parsed.user ?? null,
    };
  } catch {
    return { token: null, user: null };
  }
}

function load(): PersistedAuth {
  if (typeof window === 'undefined') return { token: null, user: null };
  return parsePersisted(window.localStorage.getItem(AUTH_STORAGE_KEY));
}

function save(state: PersistedAuth) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota issues
  }
}

const initial = load();

export const useAuth = create<AuthState>((set, get) => ({
  token: initial.token,
  user: initial.user,
  setToken: (token) => {
    const current = get();
    const next = {
      token,
      user: current.token === token ? current.user : null,
    };
    set(next);
    save(next);
  },
  setAuth: (token, user) => {
    set({ token, user });
    save({ token, user });
  },
  setUser: (user) => {
    set({ user });
    save({ token: get().token, user });
  },
  logout: () => {
    set({ token: null, user: null });
    save({ token: null, user: null });
  },
}));

export function getAuthToken(): string | null {
  return useAuth.getState().token;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== AUTH_STORAGE_KEY && event.key !== null) return;
    const next = event.key === null
      ? { token: null, user: null }
      : parsePersisted(event.newValue);
    useAuth.setState(next);
  });
}
